import type React from 'react';
import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AlertOctagonFill from '@/assets/icons/alert_octagon_fill.svg?component';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { PenSummary } from '@/domain/operationalDashboard';

import styles from './AddActivityModal.module.scss';

interface AddActivityModalProps {
    isOpen: boolean;
    locationId: number;
    /**
     * Every pen at the location — including bioscope-less ones — so operators can
     * log activities (e.g. "moved bioscope here", "drained pen") against any pen,
     * not just the bioscope-assigned ones shown in the grid.
     */
    pens: PenSummary[];
    onClose: () => void;
    onSubmit: (params: {
        locationId: number;
        title: string;
        cageId?: number;
        performedAt?: string;
        comment?: string;
    }) => void;
}

interface FormState {
    title: string;
    cageId: string;
    date: Date | null;
    time: string;
    comment: string;
}

const initialFormState: FormState = {
    title: '',
    cageId: '',
    date: null,
    time: '',
    comment: '',
};

export default function AddActivityModal(props: AddActivityModalProps) {
    const { isOpen, locationId, pens, onClose, onSubmit } = props;
    const { t } = useTranslation();
    // The time sits in the date's field, so it needs an id of its own rather than the field's.
    const timeInputId = useId().replace(/:/g, '');
    const [form, setForm] = useState<FormState>(initialFormState);

    const updateField = useCallback(
        <K extends keyof FormState>(key: K) =>
            (value: FormState[K]) =>
                setForm((prev) => ({ ...prev, [key]: value })),
        []
    );

    const handleClose = useCallback(() => {
        setForm(initialFormState);
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(
        (ev: React.FormEvent<HTMLFormElement>) => {
            ev.preventDefault();
            let performedAt: string | undefined;
            if (form.date) {
                const d = new Date(form.date);
                if (form.time) {
                    const [hours, minutes] = form.time.split(':').map(Number);
                    d.setHours(hours, minutes, 0, 0);
                }
                performedAt = d.toISOString();
            }
            onSubmit({
                locationId,
                title: form.title.trim(),
                cageId: form.cageId ? Number(form.cageId) : undefined,
                performedAt,
                comment: form.comment.trim() || undefined,
            });
            setForm(initialFormState);
            onClose();
        },
        [form, locationId, onSubmit, onClose]
    );

    const isValid = useMemo(() => form.title.trim().length > 0, [form.title]);

    const penOptions = useMemo(
        () => pens.map((p) => ({ value: String(p.cageId), label: p.cageName })),
        [pens]
    );

    return (
        <ModalComponent
            className={styles.modalDialog}
            isOpen={isOpen}
            onClose={handleClose}
            keyboard
            centered
            title={t('operationalDashboard.addActivityModal.title')}
            titleIcon={AlertOctagonFill}
            closeTrackingId="operational_add_activity_close">
            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <div className={styles.description}>
                        {t('operationalDashboard.addActivityModal.description')}
                    </div>

                    <FormField label={t('operationalDashboard.addActivityModal.whatWasDone')}>
                        <TextField
                            type="text"
                            value={form.title}
                            onChange={(e) => updateField('title')(e.target.value)}
                            placeholder={t(
                                'operationalDashboard.addActivityModal.whatWasDonePlaceholder'
                            )}
                            required
                        />
                    </FormField>

                    <FormField label={t('operationalDashboard.addActivityModal.whichPen')}>
                        <Select
                            value={form.cageId}
                            onChange={(e) => updateField('cageId')(e.target.value)}>
                            <option value="">
                                {t('operationalDashboard.addActivityModal.selectPen')}
                            </option>
                            {penOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label={t('operationalDashboard.addActivityModal.when')}>
                        <div className={styles.dateTimeRow}>
                            <div className={styles.dateField}>
                                <StyledSingleDatePicker
                                    date={form.date}
                                    onDateChange={updateField('date')}
                                />
                            </div>
                            <div className={styles.timeField}>
                                <TextField
                                    id={timeInputId}
                                    aria-label={t('operationalDashboard.addActivityModal.when')}
                                    type="time"
                                    value={form.time}
                                    onChange={(e) => updateField('time')(e.target.value)}
                                />
                            </div>
                        </div>
                    </FormField>

                    <FormField label={t('operationalDashboard.addActivityModal.why')}>
                        <TextField
                            type="text"
                            value={form.comment}
                            onChange={(e) => updateField('comment')(e.target.value)}
                            placeholder={t('operationalDashboard.addActivityModal.whyPlaceholder')}
                        />
                    </FormField>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="secondary"
                        trackingId="operational_add_activity_cancel"
                        onClick={handleClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        trackingId="operational_add_activity_submit"
                        disabled={!isValid}>
                        {t('operationalDashboard.addActivityModal.confirm')}
                    </Button>
                </ModalFooter>
            </form>
        </ModalComponent>
    );
}
